import { Deal, DealSourceEnum, DealStateEnum } from '../gql/dealGql';
import { DealType, StructuringFollowUpDealFragmentFragment } from '../gql/generated/graphql';

import {
  PermissionEnum,
  dealSourceToPermissionMap,
  dealTypeToPermissionMap,
  editingPermissions,
  readOnlyDealStates,
} from '../constants/permissions';
import { User } from '../types/user';

import { equalsAny } from './arrays';

interface IsInputReadOnlyParams {
  abilities: Set<PermissionEnum>;
  dealState: DealStateEnum;
  determineReadOnlyByDealState?: boolean;
  isEditing?: boolean;
}

export const isInputReadOnly = ({
  abilities,
  dealState,
  determineReadOnlyByDealState,
  isEditing,
}: IsInputReadOnlyParams) => {
  const userReadOnlyDealStates = [...readOnlyDealStates];

  if (abilities.has(PermissionEnum.Setter)) {
    userReadOnlyDealStates.push(DealStateEnum.Closing);
  }

  const hasEditingPermission = editingPermissions.some((editingPermission) =>
    abilities.has(editingPermission),
  );
  if (isEditing && hasEditingPermission) {
    const indexOfClosedState = userReadOnlyDealStates.indexOf(DealStateEnum.Closed);
    userReadOnlyDealStates.splice(indexOfClosedState, 1);
  }

  const canEditFundedOnwards = abilities.has(PermissionEnum.EditFundedOnwards);
  if (isEditing && canEditFundedOnwards) {
    const indexOfFundedState = userReadOnlyDealStates.indexOf(DealStateEnum.Funded);
    userReadOnlyDealStates.splice(indexOfFundedState, 1);
  }

  if (
    determineReadOnlyByDealState ||
    abilities.has(PermissionEnum.Closer) ||
    abilities.has(PermissionEnum.FundingClerk)
  ) {
    return userReadOnlyDealStates.includes(dealState);
  }

  return userReadOnlyDealStates.includes(dealState) || !abilities.has(PermissionEnum.BuildDeal);
};

export const getAllowedDealSources = (abilities: Set<PermissionEnum>) => {
  const allSources = Object.values(DealSourceEnum);

  const allowedSources = allSources.filter((source) => {
    const permission = dealSourceToPermissionMap.get(source);

    return permission && abilities.has(permission);
  });

  return allowedSources;
};

export const getAllowedDealTypes = (abilities: Set<PermissionEnum>) => {
  const allTypes = Object.values(DealType);

  return allTypes.filter((type) => {
    const permission = dealTypeToPermissionMap.get(type);

    // Types without a permission mapping are visible to everyone
    return !permission || abilities.has(permission);
  });
};

export const isAllSources = (sources: DealSourceEnum[]) => {
  const allSources = Object.values(DealSourceEnum);
  return sources.length === allSources.length;
};

export const isAllTypes = (types: DealType[]) => {
  const allTypes = Object.values(DealType);
  return types.length === allTypes.length;
};

export const canManuallyClaimSetter = (
  deal: Deal,
  permissions: Set<PermissionEnum>,
  options: { ignoreSource: boolean } = { ignoreSource: false },
): boolean =>
  permissions.has(PermissionEnum.ClaimDealAsAdvisor) &&
  (options.ignoreSource || deal.source === DealSourceEnum.Web) &&
  [DealStateEnum.Estimate, DealStateEnum.SoftClose].includes(deal.state);

export const canManuallyClaimStructuringManager = (
  deal: Deal | StructuringFollowUpDealFragmentFragment,
  permissions: Set<PermissionEnum>,
): boolean =>
  permissions.has(PermissionEnum.ClaimDealAsStructuringManager) &&
  DealStateEnum.Structuring === deal.state;

export const isSetterUnclaimed = <T extends Pick<Deal, 'setter'>>(d: T): boolean => !d.setter?.id;
export const isStructuringManagerClaimed = (d: Deal | StructuringFollowUpDealFragmentFragment) =>
  !!d.structuring_manager_id;

const canClaimFinancialSpecialistInDealState = (
  permissions: Set<PermissionEnum>,
  dealState: DealStateEnum,
) => {
  const canClaimInSoftClose = permissions.has(PermissionEnum.ClaimFinancialSpecialistInSoftClose);
  const unclaimableDealStates = [DealStateEnum.Estimate, DealStateEnum.Floor];

  if (!canClaimInSoftClose) {
    unclaimableDealStates.push(DealStateEnum.SoftClose);
  }

  return !unclaimableDealStates.includes(dealState);
};

export const isAdvisor = (permissions: Set<PermissionEnum>) =>
  permissions.has(PermissionEnum.Setter) || permissions.has(PermissionEnum.SalesAdvisor);

export const isFinancialSpecialist = (permissions: Set<PermissionEnum>) =>
  permissions.has(PermissionEnum.Closer) || permissions.has(PermissionEnum.FinancialSpecialist);

export const isLeadAdvisor = (permissions: Set<PermissionEnum>) =>
  permissions.has(PermissionEnum.LeadAdvisorPod);

export const canManuallyClaimCloser = (
  deal: Deal,
  permissions: Set<PermissionEnum>,
  user: User,
): boolean =>
  !!user.can_claim_as_closer &&
  (permissions.has(PermissionEnum.Closer) ||
    permissions.has(PermissionEnum.SuperUser) ||
    permissions.has(PermissionEnum.FinancialSpecialist)) &&
  canClaimFinancialSpecialistInDealState(permissions, deal.state);

export const isCloserUnclaimed = <T extends Pick<Deal, 'closer' | 'closer2'>>(d: T): boolean =>
  !d.closer?.id && !d.closer2?.id;

export const canManuallyClaimFundingClerk = (
  deal: Deal,
  permissions: Set<PermissionEnum>,
): boolean =>
  (permissions.has(PermissionEnum.SuperUser) ||
    permissions.has(PermissionEnum.ClaimDealAsAccountingClerk)) &&
  deal.state === DealStateEnum.Signed;

export const isFundingClerkUnclaimed = (d: Deal): boolean => !d.funding_clerk?.id;

export const canManuallyClaimRanger = (deal: Deal, permissions: Set<PermissionEnum>): boolean =>
  permissions.has(PermissionEnum.ClaimBootRequest) &&
  deal.state === DealStateEnum.Closing &&
  deal.request_boot === true;

export const isRangerUnclaimed = (d: Deal): boolean => !d.second_turn_financial_specialist?.id;

/**
 * Returns the role if the user can claim the deal as that role.
 * Returns null if the user cannot claim the deal or is already claimed.
 */
export const getClaimRole = (
  deal: Deal,
  abilities: Set<PermissionEnum>,
  user: User,
):
  | keyof Pick<
      Deal,
      | 'setter_id'
      | 'closer_id'
      | 'funding_clerk_id'
      | 'second_turn_financial_specialist_id'
      | 'structuring_manager_id'
    >
  | null => {
  if (
    deal.state === DealStateEnum.StructuringInProgress ||
    deal.state === DealStateEnum.Structuring
  ) {
    // Used to see if deal info needs to be blurred for structuring manager.
    if (!isStructuringManagerClaimed(deal) && canManuallyClaimStructuringManager(deal, abilities)) {
      return 'structuring_manager_id';
    }
    return null;
  }

  // Check for Ranger claim first since it has specific conditions.
  if (isRangerUnclaimed(deal) && canManuallyClaimRanger(deal, abilities)) {
    return 'second_turn_financial_specialist_id';
  }

  // The `canClaim` functions also check the deal state.
  if (isCloserUnclaimed(deal) && canManuallyClaimCloser(deal, abilities, user)) {
    return 'closer_id';
  }
  if (isSetterUnclaimed(deal) && canManuallyClaimSetter(deal, abilities)) {
    return 'setter_id';
  }
  if (isFundingClerkUnclaimed(deal) && canManuallyClaimFundingClerk(deal, abilities)) {
    return 'funding_clerk_id';
  }

  return null;
};

export const isClaimRequired = (deal: Deal, abilities: Set<PermissionEnum>, user: User) =>
  getClaimRole(deal, abilities, user) !== null;

// Similar logic as `filterOwner` modifier in `types/deal.ts` in API. Keep in sync.
export const filterOwner = (deals: Deal[], user: User, abilities: Set<PermissionEnum>): Deal[] =>
  deals.filter((deal) => {
    const isOwner = equalsAny(user.id, [
      deal.setter?.id,
      deal.closer?.id,
      deal.closer2?.id,
      deal.second_turn_financial_specialist?.id,
    ]);

    if (!abilities.has(PermissionEnum.SeeBootRequest) && deal.request_boot === true) {
      return false;
    }

    // Non-Rangers should NOT see claimed boot requested deals in `Closing`.
    if (
      !abilities.has(PermissionEnum.ClaimBootRequest) &&
      deal.state === DealStateEnum.Closing &&
      deal.request_boot === true &&
      deal.second_turn_financial_specialist?.id
    ) {
      return false;
    }

    return (
      isOwner ||
      (isSetterUnclaimed(deal) &&
        canManuallyClaimSetter(deal, abilities, { ignoreSource: true })) ||
      (isCloserUnclaimed(deal) && canManuallyClaimCloser(deal, abilities, user)) ||
      (isRangerUnclaimed(deal) && canManuallyClaimRanger(deal, abilities))
    );
  });
